import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { findModule, MASTER_MODULES } from "@/lib/masterConfig";
import { PageHeader, Card, Badge, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Upload, Download, Trash2, Pencil, RotateCcw, ChevronLeft, ChevronRight, X, Filter as FilterIcon, ShieldCheck } from "lucide-react";
import HotelProfileDirect from "@/pages/master/HotelProfileDirect";
import BillingSettings from "@/pages/master/BillingSettings";
import { useHotelContext } from "@/contexts/HotelContext";
import { currencySymbol } from "@/lib/currency";

const lookupCache = new Map(); // key: `${moduleKey}|${labelField}` -> {id: label}

async function loadLookup(moduleKey, labelField = "name") {
  const cacheKey = `${moduleKey}|${labelField}`;
  if (lookupCache.has(cacheKey)) return lookupCache.get(cacheKey);
  try {
    const { data } = await api.get(`/master/${moduleKey}/?page_size=500`);
    const map = {};
    data.items.forEach((r) => {
      let label = r[labelField];
      // Sensible fallbacks: name → floor_name+floor_number → code → id-tail
      if (!label) {
        if (r.floor_number != null && r.floor_name) label = `${r.floor_number} · ${r.floor_name}`;
        else if (r.floor_name) label = r.floor_name;
        else if (r.floor_number != null) label = `Floor ${r.floor_number}`;
        else label = r.name || r.code || String(r.id).slice(-6);
      }
      map[r.id] = label;
    });
    lookupCache.set(cacheKey, map);
    return map;
  } catch { return {}; }
}

function FieldInput({ field, value, onChange, lookups, currencySym }) {
  const val = value ?? (field.default !== undefined ? field.default : "");
  if (field.type === "textarea") return (
    <Textarea value={val || ""} onChange={(e) => onChange(e.target.value)} rows={3}
      className="bg-hocp-bgCard border-hocp-border" data-testid={`field-${field.key}`}/>
  );
  if (field.type === "select") return (
    <Select value={val || ""} onValueChange={onChange}>
      <SelectTrigger className="bg-hocp-bgCard border-hocp-border" data-testid={`field-${field.key}`}><SelectValue placeholder={`Select ${field.label}`}/></SelectTrigger>
      <SelectContent>{field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
  if (field.type === "lookup") {
    const opts = lookups[field.lookup] || {};
    return (
      <Select value={val || ""} onValueChange={onChange}>
        <SelectTrigger className="bg-hocp-bgCard border-hocp-border" data-testid={`field-${field.key}`}><SelectValue placeholder={`Select ${field.label}`}/></SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {Object.entries(opts).map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "boolean") return (
    <div className="flex items-center gap-2"><Switch checked={!!val} onCheckedChange={onChange} data-testid={`field-${field.key}`}/><span className="text-xs text-hocp-textMuted">{val ? "Yes" : "No"}</span></div>
  );
  if (field.type === "chips") return <ChipsInput field={field} value={val} onChange={onChange}/>;
  if (field.money) {
    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-hocp-textMuted text-[13px] mono pointer-events-none">{currencySym || "$"}</span>
        <Input type="number" step={field.step || "0.01"} value={val ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          className="bg-hocp-bgCard border-hocp-border pl-7 mono tabular-nums" required={field.required}
          data-testid={`field-${field.key}`}/>
      </div>
    );
  }
  return (
    <Input type={field.type === "number" ? "number" : (field.type === "date" ? "date" : (field.type === "email" ? "email" : "text"))}
      step={field.step} value={val ?? ""} onChange={(e) => onChange(field.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
      className={`bg-hocp-bgCard border-hocp-border ${field.mono ? "mono" : ""}`} required={field.required}
      placeholder={field.placeholder}
      data-testid={`field-${field.key}`}/>
  );
}

function ChipsInput({ field, value, onChange }) {
  // Raw text kept locally while typing so users can freely type commas/spaces
  // without the parser stripping them mid-keystroke. Committed to the outer
  // array-shaped `onChange` on blur or Enter.
  const toArr = (v) => Array.isArray(v)
    ? v
    : (v ? String(v).split(",").map((s) => s.trim()).filter(Boolean) : []);
  const toStr = (v) => toArr(v).join(", ");

  const [text, setText] = useState(toStr(value));

  // Sync when the parent value changes (e.g. dialog opens with a different row).
  useEffect(() => { setText(toStr(value)); }, [
    Array.isArray(value) ? value.join("\u0000") : value,
  ]);

  const commit = () => {
    const parsed = text.split(",").map((s) => s.trim()).filter(Boolean);
    onChange(parsed);
    setText(parsed.join(", "));
  };

  return (
    <Input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        }
      }}
      className={`bg-hocp-bgCard border-hocp-border ${field.mono ? "mono" : ""}`}
      placeholder="comma, separated, values"
      data-testid={`field-${field.key}`}
    />
  );
}

function renderCell(col, row, lookups, currencySym) {
  const v = row[col.key];
  if (v == null || v === "") return <span className="text-zinc-700">—</span>;
  if (col.badge) {
    const tone = col.badge[v] || "neutral";
    return <Badge tone={tone}>{String(v).toUpperCase()}</Badge>;
  }
  if (col.lookup) {
    const label = lookups[col.lookup]?.[v];
    return <span className="text-hocp-textPrimary text-[12px]">{label || <span className="mono text-hocp-textTertiary">{String(v).slice(-8)}</span>}</span>;
  }
  if (col.money) return <span className="mono tabular-nums">{currencySym || "$"}{Number(v).toFixed(2)}</span>;
  if (col.boolean) return v ? <Badge tone="success">YES</Badge> : <Badge tone="neutral">NO</Badge>;
  if (col.chips && Array.isArray(v)) return (
    <div className="flex flex-wrap gap-1">{v.slice(0, 4).map((x, i) => <span key={i} className="mono text-[10px] bg-hocp-bgSubtle px-1.5 py-0.5 rounded text-hocp-textMuted">{x}</span>)}{v.length > 4 && <span className="text-[10px] text-hocp-textMuted">+{v.length - 4}</span>}</div>
  );
  return <span className={col.mono ? "mono text-hocp-textMuted" : "text-hocp-textPrimary"}>{String(v)}</span>;
}

export default function MasterPage() {
  const { moduleKey } = useParams();
  // ADR-WF-1 v2.1: Hotel Profiles inside the Hotel Shell always renders the
  // single active hotel's profile — never a list, never a hotel selector.
  if (moduleKey === "hotel-profiles") return <HotelProfileDirect />;
  if (moduleKey === "billing-settings") return <BillingSettings />;
  return <MasterPageInner moduleKey={moduleKey} />;
}

function MasterPageInner({ moduleKey }) {
  const { ctx } = useHotelContext() || {};
  const currencySym = currencySymbol(ctx?.currency);

  const cfg = findModule(moduleKey);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filters, setFilters] = useState({});
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [lookups, setLookups] = useState({});
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null); // record or null
  const [form, setForm] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  // Build list of all lookup module keys we need
  const lookupKeys = useMemo(() => {
    if (!cfg) return [];
    const keys = new Set();
    cfg.columns.forEach((c) => c.lookup && keys.add(c.lookup));
    cfg.fields.forEach((f) => f.type === "lookup" && keys.add(f.lookup));
    (cfg.filters || []).forEach((f) => f.type === "lookup" && keys.add(f.lookup));
    return [...keys];
  }, [cfg]);

  // Build map of lookup module -> preferred labelField (respecting
  // `lookupField` overrides from columns/fields/filters).
  const lookupLabels = useMemo(() => {
    if (!cfg) return {};
    const m = {};
    const setLabel = (moduleKey, labelField) => {
      if (!moduleKey) return;
      if (labelField && !m[moduleKey]) m[moduleKey] = labelField;
    };
    cfg.columns.forEach((c) => c.lookup && setLabel(c.lookup, c.lookupField));
    cfg.fields.forEach((f) => f.type === "lookup" && setLabel(f.lookup, f.lookupField));
    (cfg.filters || []).forEach((f) => f.type === "lookup" && setLabel(f.lookup, f.lookupField));
    return m;
  }, [cfg]);

  // Load lookups
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const all = {};
      for (const k of lookupKeys) {
        let labelField = lookupLabels[k];
        if (!labelField) {
          const f = MASTER_MODULES.find((m) => m.key === k);
          labelField = f?.columns?.find?.((c) => c.key === "name") ? "name" : "code";
        }
        all[k] = await loadLookup(k, labelField);
      }
      if (!cancelled) setLookups(all);
    })();
    return () => { cancelled = true; };
  }, [lookupKeys, lookupLabels]);

  const load = useCallback(async () => {
    if (!cfg) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        include_deleted: String(includeDeleted),
      });
      if (q) params.set("q", q);
      const fStr = Object.entries(filters).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}:${v}`).join(",");
      if (fStr) params.set("filters", fStr);
      const { data } = await api.get(`/master/${cfg.key}/?${params}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [cfg, page, pageSize, q, filters, includeDeleted]);

  useEffect(() => { setPage(1); }, [moduleKey]);
  useEffect(() => { load(); }, [load]);

  // Reset when module changes
  useEffect(() => { setQ(""); setFilters({}); setIncludeDeleted(false); lookupCache.clear(); }, [moduleKey]);

  // Group fields for the form (must be before any conditional return; safe when cfg is null)
  // For hotel-scoped modules the `hotel_id` picker is redundant — the
  // backend auto-stamps it from the active hotel context, so we hide it.
  const isHotelScoped = cfg && cfg.key !== "hotel-profiles" &&
    (cfg.fields || []).some(f => f.key === "hotel_id");
  const fieldGroups = useMemo(() => {
    const groups = {};
    (cfg?.fields || [])
      .filter((f) => !(isHotelScoped && f.key === "hotel_id"))
      .forEach((f) => { const g = f.group || "Details"; (groups[g] = groups[g] || []).push(f); });
    return groups;
  }, [cfg, isHotelScoped]);
  const visibleColumns = useMemo(
    () => (cfg?.columns || []).filter((c) => !(isHotelScoped && c.key === "hotel_id")),
    [cfg, isHotelScoped]
  );

  if (!cfg) return <div className="text-hocp-textMuted text-sm">Module not found.</div>;

  const openCreate = () => {
    const init = {};
    cfg.fields.forEach((f) => { if (f.default !== undefined) init[f.key] = f.default; });
    setForm(init); setEditing(null); setDlgOpen(true);
  };
  const openEdit = (row) => { setForm({ ...row }); setEditing(row); setDlgOpen(true); };

  const submit = async () => {
    try {
      const payload = { ...form };
      // strip server-controlled fields
      ["id", "created_at", "updated_at", "created_by", "updated_by", "is_deleted", "deleted_at"].forEach((k) => delete payload[k]);
      // For hotel-scoped modules, the backend auto-stamps hotel_id from
      // the active hotel context — never let the client set it.
      if (isHotelScoped) delete payload.hotel_id;
      if (editing) {
        await api.patch(`/master/${cfg.key}/${editing.id}`, payload);
        toast.success(`${cfg.title} updated`);
      } else {
        await api.post(`/master/${cfg.key}/`, payload);
        toast.success(`${cfg.title} created`);
      }
      setDlgOpen(false); load();
      lookupCache.delete(cfg.key);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (row) => {
    setPendingDelete(row);
  };
  const confirmRemove = async () => {
    if (!pendingDelete) return;
    try { await api.delete(`/master/${cfg.key}/${pendingDelete.id}`); toast.success("Deleted"); setPendingDelete(null); load(); }
    catch (e) { toast.error(formatApiError(e)); setPendingDelete(null); }
  };
  const restore = async (row) => {
    try { await api.post(`/master/${cfg.key}/${row.id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (includeDeleted) params.set("include_deleted", "true");
    const fStr = Object.entries(filters).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}:${v}`).join(",");
    if (fStr) params.set("filters", fStr);
    window.open(`${API_BASE}/master/${cfg.key}/export.csv?${params}`, "_blank");
  };

  const importCsv = async () => {
    if (!importFile) return toast.error("Choose a CSV file first");
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const { data } = await api.post(`/master/${cfg.key}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} created · ${data.updated} updated · ${data.failed.length} failed`);
      setImportOpen(false); setImportFile(null); load();
      lookupCache.delete(cfg.key);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div data-testid={`master-page-${cfg.key}`}>
      <PageHeader
        title={cfg.title}
        subtitle={cfg.subtitle}
        action={
          <div className="flex gap-2">
            {!cfg.disableImport && (
              <Button variant="outline" onClick={() => setImportOpen(true)} className="h-9 px-3 text-[13px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="import-csv-button">
                <Upload className="w-3.5 h-3.5 mr-1.5"/> Import
              </Button>
            )}
            <Button variant="outline" onClick={exportCsv} className="h-9 px-3 text-[13px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="export-csv-button">
              <Download className="w-3.5 h-3.5 mr-1.5"/> Export
            </Button>
            {!cfg.disableCreate && (
              <Button onClick={openCreate} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="create-button">
                <Plus className="w-3.5 h-3.5 mr-1.5"/> New
              </Button>
            )}
          </div>
        }
      />

      {cfg.readOnlyBanner && (
        <div
          className="flex items-start gap-3 rounded-lg border border-blue-300 dark:border-blue-400/40 bg-blue-50 dark:bg-blue-500/15 px-4 py-3 text-[13px] text-blue-900 dark:text-blue-100 mb-4"
          data-testid="master-readonly-banner"
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-600 dark:text-blue-300 shrink-0" />
          <div className="font-medium">{cfg.readOnlyBanner}</div>
        </div>
      )}

      {/* Toolbar: search + filters */}
      <Card className="p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-3.5 h-3.5 text-hocp-textMuted absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder={`Search ${cfg.title.toLowerCase()}…`} aria-label={`Search ${cfg.title.toLowerCase()}`}
              className="bg-hocp-bgCard border-hocp-border pl-9 h-9 text-[13px]" data-testid="search-input"/>
          </div>
          {(cfg.filters || [])
            .filter((f) => !(isHotelScoped && f.key === "hotel_id"))
            .map((f) => (
            <div key={f.key} className="min-w-[160px]">
              {f.type === "select" ? (
                <Select value={filters[f.key] || ""} onValueChange={(v) => { setFilters({ ...filters, [f.key]: v === "__all__" ? "" : v }); setPage(1); }}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border h-9 text-[13px]" data-testid={`filter-${f.key}`} aria-label={`Filter by ${f.label}`}>
                    <SelectValue placeholder={f.label}/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All {f.label}</SelectItem>
                    {f.options.filter(o => o.value).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === "lookup" ? (
                <Select value={filters[f.key] || ""} onValueChange={(v) => { setFilters({ ...filters, [f.key]: v === "__all__" ? "" : v }); setPage(1); }}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border h-9 text-[13px]" data-testid={`filter-${f.key}`} aria-label={`Filter by ${f.label}`}>
                    <SelectValue placeholder={f.label}/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All {f.label}</SelectItem>
                    {Object.entries(lookups[f.lookup] || {}).map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          ))}
          <label className="flex items-center gap-2 px-2 text-[12px] text-hocp-textMuted ml-auto">
            <Checkbox checked={includeDeleted} onCheckedChange={(v) => { setIncludeDeleted(!!v); setPage(1); }} data-testid="include-deleted-checkbox"/>
            <span>Show deleted</span>
          </label>
          {(q || Object.values(filters).some(v => v)) && (
            <Button variant="ghost" onClick={() => { setQ(""); setFilters({}); setPage(1); }} className="h-9 px-2 text-[12px] text-hocp-textMuted" data-testid="clear-filters">
              <X className="w-3 h-3 mr-1"/> Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Table */}
      <div className="border border-hocp-border rounded-md bg-hocp-bgCard overflow-hidden" data-testid="master-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-hocp-border bg-hocp-bgSubtle">
                {visibleColumns.map((c) => (
                  <th key={c.key} className={`px-4 py-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-hocp-textMuted mono text-left ${c.align === "right" ? "text-right" : ""}`} style={{ width: c.width }}>
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-hocp-textMuted mono text-right w-[110px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && !loading ? (
                <tr><td colSpan={visibleColumns.length + 1} className="px-4 py-10 text-center text-xs text-hocp-textMuted">No records.</td></tr>
              ) : data.items.map((row) => (
                <tr key={row.id} className={`border-b border-hocp-border last:border-0 hover:bg-hocp-bgSubtle/70 transition-colors ${row.is_deleted ? "opacity-50" : ""}`} data-testid={`row-${row.code || row.id}`}>
                  {visibleColumns.map((c) => (
                    <td key={c.key} className={`px-4 py-2.5 text-[12px] ${c.align === "right" ? "text-right" : ""}`}>
                      {renderCell(c, row, lookups, currencySym)}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right">
                    {row.is_deleted ? (
                      <Button size="sm" variant="ghost" onClick={() => restore(row)} className="h-7 px-2 text-[11px] mono uppercase text-blue-400" data-testid={`restore-${row.id}`}><RotateCcw className="w-3 h-3 mr-1"/>Restore</Button>
                    ) : (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="h-7 w-7 p-0 text-hocp-textMuted hover:text-hocp-deepTeal" data-testid={`edit-${row.code || row.id}`}><Pencil className="w-3.5 h-3.5"/></Button>
                        {!cfg.disableDelete && (
                          <Button size="sm" variant="ghost" onClick={() => remove(row)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-${row.code || row.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-[11px] mono text-hocp-textMuted">
        <span>{data.total} records · page {page} of {totalPages}{loading ? " · loading…" : ""}</span>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 px-2" data-testid="page-prev"><ChevronLeft className="w-3.5 h-3.5"/></Button>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 px-2" data-testid="page-next"><ChevronRight className="w-3.5 h-3.5"/></Button>
        </div>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Create"} {cfg.title}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {Object.entries(fieldGroups).map(([gname, fields]) => (
              <div key={gname}>
                {Object.keys(fieldGroups).length > 1 && (
                  <div className="text-[10px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-2 pb-2 border-b border-hocp-border">{gname}</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {fields.map((f) => (
                    <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                      <Label className="text-xs uppercase tracking-wider text-hocp-textMuted">
                        {f.label}{f.required && <span className="text-red-400 ml-0.5">*</span>}
                      </Label>
                      <div className="mt-1.5">
                        <FieldInput field={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} lookups={lookups} currencySym={currencySym}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {editing && (
              <div className="flex items-center gap-2 pt-2 border-t border-hocp-border">
                <Switch checked={form.is_active ?? true} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="field-is_active"/>
                <span className="text-xs text-hocp-textMuted">Active</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={submit} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-form-button">
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Import {cfg.title} (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-hocp-textMuted leading-relaxed">
              Upload a CSV with the same columns produced by Export. Boolean values: <span className="mono">true/false</span>. Lists/arrays as JSON. Rows matching an existing <span className="mono">{cfg.fields.find(f => f.required && f.mono)?.key || "code"}</span> will update; new ones will insert.
            </div>
            <Input type="file" accept=".csv" onChange={(e) => setImportFile(e.target.files?.[0])}
              className="bg-hocp-bgCard border-hocp-border file:text-hocp-textPrimary file:bg-transparent file:border-0 file:text-xs" data-testid="import-file-input"/>
          </div>
          <DialogFooter><Button onClick={importCsv} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-import-button">Import</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete {cfg.title.slice(0, -1) || cfg.title}?</AlertDialogTitle>
            <AlertDialogDescription className="text-hocp-textMuted text-[13px]">
              <span className="mono text-hocp-textPrimary">{pendingDelete?.code || pendingDelete?.name || pendingDelete?.id}</span> will be soft-deleted and hidden from lists. You can restore it later via &ldquo;Show deleted&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="row-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-red-500 hover:bg-red-600 text-white" data-testid="row-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
